import { DataGrid } from '@mui/x-data-grid'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { getAllModels } from '../../graphql/queries'
import { Box, IconButton, Chip, FormControl, InputLabel, Select, MenuItem } from '@mui/material'
import { useState, useEffect } from 'react'
import TextField from '@mui/material/TextField'
import CloseIcon from '@mui/icons-material/Close'

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'starting':
      return 'info'
    case 'processing':
      return 'warning'
    case 'succeeded':
      return 'success'
    case 'canceled':
      return 'default'
    case 'failed':
      return 'error'
    default:
      return 'default'
  }
}

const getStatusLabel = (status) => {
  if (!status) return 'Unknown'
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
}

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'starting', label: 'Starting' },
  { value: 'processing', label: 'Processing' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'failed', label: 'Failed' },
]

const columns = [
  {
    field: 'tableIndex',
    headerName: '#',
    flex: 0.2,
    sortable: false,
    filterable: false,
  },
  { field: 'name', headerName: 'Name', flex: 2.5 },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    renderCell: params => (
      <Chip
        label={getStatusLabel(params.value)}
        color={getStatusColor(params.value)}
        size="small"
        variant="filled"
        sx={{
          fontWeight: 'bold',
          minWidth: '80px',
          '& .MuiChip-label': {
            px: 1.5
          }
        }}
      />
    ),
  },
  {
    field: 'image',
    headerName: 'Banner',
    flex: 1.5,
    sortable: false,
    renderCell: params => (
      <img
        src={params.value}
        alt="Avatar"
        style={{
          width: 'clamp(42px, 10vw, 112px)',
          height: 'clamp(42px, 10vw, 112px)',
          borderRadius: '50%',
          objectFit: 'cover',
          border: '1px solid #ddd',
        }}
      />
    ),
  },
]

export default function ModelTable() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialSearch = searchParams.get('search') || ''
  const initialPage = parseInt(searchParams.get('page')) || 0
  const initialPageSize = parseInt(searchParams.get('pageSize')) || 50
  const initialStatus = searchParams.get('status') || ''

  const navigate = useNavigate()
  const [search, setSearch] = useState(initialSearch)
  const [page, setPage] = useState(initialPage)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [statusFilter, setStatusFilter] = useState(initialStatus)

  const [rowsData, setRowsData] = useState([])
  const [totalCount, setTotalCount] = useState(0)

  useEffect(() => {
    setSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      status: statusFilter,
    })
  }, [page, pageSize, search, statusFilter])

  const { data, loading, error } = useQuery(getAllModels, {
    variables: {
      page: page + 1,
      pageSize,
      name: search || null,
      status: statusFilter || null,
    },
    fetchPolicy: 'cache-and-network',
  })

  useEffect(() => {
    if (data?.getAllModelsAdmin?.models) {
      setRowsData(data.getAllModelsAdmin.models)
      setTotalCount(data.getAllModelsAdmin.totalCount)
    }
  }, [data])

  const handleRowClick = params => {
    navigate(`/models/${params.id}`)
  }

  const handleSearchChange = event => {
    setSearch(event.target.value)
    setPage(0)
  }

  const handleStatusChange = event => {
    setStatusFilter(event.target.value)
    setPage(0)
  }

  const handlePaginationModelChange = newModel => {
    setPage(newModel.page)
    setPageSize(newModel.pageSize)
  }

  const processedRows = rowsData.map((row, index) => ({
    ...row,
    tableIndex: page * pageSize + index + 1,
  }))

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          id="standard-basic"
          name="name"
          label="Name"
          variant="standard"
          value={search}
          onChange={handleSearchChange}
          sx={{ width: '250px' }}
        />
        {search && (
          <IconButton onClick={() => setSearch('')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
        <FormControl variant="standard" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={handleStatusChange}
            label="Status"
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {statusFilter && (
          <IconButton onClick={() => setStatusFilter('')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <DataGrid
        disableColumnMenu
        rows={processedRows}
        columns={columns}
        rowHeight={120}
        paginationMode="server"
        rowCount={totalCount}
        onRowClick={handleRowClick}
        loading={loading}
        pagination
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[25, 50, 100]}
        sx={{
          height: 680,
          fontSize: '13px',
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight) > .MuiDataGrid-cell': {
            whiteSpace: 'break-spaces',
          },
          '.MuiFormControl-root': {
            margin: '0px 0px 10px 0px',
            width: '100px',
          },
          '.MuiOutlinedInput-root': {
            width: '150px',
          },
          '.MuiToolbar-regular': {
            padding: 0,
          },
        }}
      />
    </Box>
  )
}
